import type { EditorBody } from "@/components/document-editor"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { uploadEditorFile } from "@/lib/upload"
import { router } from "@inertiajs/react"
import { IconTrash } from "@tabler/icons-react"
import { Suspense, lazy, useState } from "react"
import type { Comment } from "../../Boards/types"

const DocumentEditor = lazy(() =>
  import("@/components/document-editor").then((module) => ({ default: module.DocumentEditor })),
)

function when(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

function hasText(body: unknown): boolean {
  if (Array.isArray(body)) return body.some(hasText)
  if (body && typeof body === "object") {
    const node = body as Record<string, unknown>
    if (typeof node.text === "string" && node.text.trim() !== "") return true
    return Object.values(node).some(hasText)
  }
  return false
}
interface CommentThreadProps {
  ticketKey: string
  comments: Comment[]
  canComment: boolean
}

export function CommentThread({ ticketKey, comments, canComment }: CommentThreadProps) {
  const [draftId, setDraftId] = useState(0)
  const [body, setBody] = useState<EditorBody>([])

  const submit = () => {
    if (!hasText(body)) return

    router.post(
      `/tickets/${ticketKey}/comments`,
      { ticket_comment: { body } },
      {
        preserveScroll: true,
        only: ["comments"],
        onSuccess: () => {
          setBody([])
          setDraftId((id) => id + 1)
        },
      },
    )
  }

  return (
    <div className="space-y-4">
      {comments.length === 0 && <p className="text-muted-foreground text-sm">No comments yet.</p>}

      {comments.map((comment) => (
        <article key={comment.id} className="border-b pb-3 last:border-b-0">
          <div className="mb-1 flex items-baseline gap-2">
            <span className="text-sm font-medium">{comment.author?.name ?? "Someone"}</span>
            <span className="text-muted-foreground text-xs">{when(comment.created_at)}</span>
            {comment.edited && <span className="text-muted-foreground text-xs">· edited</span>}

            {comment.can_destroy && (
              <Button
                variant="ghost"
                size="icon"
                className="ml-auto size-7"
                aria-label={`Delete comment by ${comment.author?.name ?? "someone"}`}
                onClick={() => router.delete(`/tickets/${ticketKey}/comments/${comment.id}`, { preserveScroll: true })}
              >
                <IconTrash className="size-3.5" />
              </Button>
            )}
          </div>

          <Suspense fallback={<Skeleton className="h-10 w-full" />}>
            <div className="editor-flush">
              <DocumentEditor initialBody={comment.body} editable={false} />
            </div>
          </Suspense>
        </article>
      ))}

      {canComment && (
        <div className="space-y-2 border-t pt-3">
          <Suspense fallback={<Skeleton className="h-20 w-full" />}>
            <div className="editor-flush">
              <DocumentEditor
                key={draftId}
                initialBody={[]}
                editable
                onChange={setBody}
                uploadFile={(file) => uploadEditorFile(`/tickets/${ticketKey}/files`, file)}
              />
            </div>
          </Suspense>

          <div className="flex justify-end">
            <Button size="sm" onClick={submit} disabled={!hasText(body)}>
              Comment
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
